import { useCallback, useEffect, useState } from "react";
import type { ProductDetail as ProductDetailDTO } from "../../../shared/types";
import { api, ApiError } from "../../lib/api";
import { productsApi, type ProductGoal, type ProfileView } from "../../lib/productsApi";
import { useOnShown } from "../../lib/inApp";
import { lastLoaded, loadKept, prefetchKept } from "../../lib/kept";

export interface ProductPageData {
  detail: ProductDetailDTO;
  profile: ProfileView;
  goals: ProductGoal[];
}

/** Downscale and re-encode a chosen logo so it stays small and fast. */
async function resizeImage(file: File, max = 512): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b ?? file), "image/jpeg", 0.85));
}

/** A product page's three reads, kept together. */
const readProduct = (id: string): Promise<ProductPageData> =>
  Promise.all([api.products.detail(id), productsApi.profile(id), productsApi.goals(id)]).then(([detail, profile, g]) => ({
    detail,
    profile,
    goals: g.goals,
  }));

/** Start loading a product's page as a finger rests on its row, so it opens ready. */
export function prefetchProduct(id: string): void {
  prefetchKept(`product:${id}`, () => readProduct(id));
}

/** A product page's data (its detail, profile and goals) and its one write, the logo. */
export function useProductPage(id: string, toast: (message: string, done?: boolean) => void) {
  const [data, setData] = useState<ProductPageData | null>(() => lastLoaded(`product:${id}`));
  const [err, setErr] = useState<ApiError | null>(null);

  const load = useCallback(() => loadKept(`product:${id}`, () => readProduct(id)), [id]);

  useEffect(() => {
    let alive = true;
    setData(lastLoaded(`product:${id}`));
    setErr(null);
    load()
      .then((d) => alive && setData(d))
      .catch((e: unknown) => alive && setErr(e instanceof ApiError ? e : new ApiError(0, String(e))));
    return () => {
      alive = false;
    };
  }, [load, id]);
  // Why quiet: the page keeps what it shows; a failed read leaves it as it is.
  useOnShown(() => load().then(setData, () => {}));

  const setLogo = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast("Choose an image file for the logo.", false);
      return;
    }
    try {
      const { product } = await api.products.uploadLogo(id, await resizeImage(file));
      setData((d) => (d ? { ...d, detail: { ...d.detail, product } } : d));
      toast("Logo saved. Partners see it too.");
    } catch (x) {
      toast(x instanceof ApiError ? x.message : "Couldn't save the logo. Try another file.", false);
    }
  };

  return { data, err, setLogo };
}
